import React, { useMemo, useState } from "react";
import { Box, Card, CardContent, Typography, TextField, Stack, useTheme } from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { PageContainer, formatCurrency } from "components/shared";
import { useGetSalesQuery } from "state/api";

const iso = (d) => d.toISOString().slice(0, 10);

const Daily = () => {
  const theme = useTheme();
  const p = theme.palette;
  const { data, isLoading } = useGetSalesQuery();

  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const series = useMemo(() => {
    const rows = (data?.data?.salesByDay ?? data?.data?.dailyData ?? []).map((d) => ({
      date: d.date || d._id,
      revenue: d.totalRevenue ?? d.totalSales ?? 0,
      units: d.totalUnits ?? 0,
    }));
    return rows
      .filter((r) => r.date && (!start || r.date >= start) && (!end || r.date <= end))
      .sort((a, b) => (a.date < b.date ? -1 : 1))
      .map((r) => ({ ...r, label: r.date.slice(5) })); // MM-DD
  }, [data, start, end]);

  const axisStyle = { fontSize: 12, fill: p.text.secondary };

  return (
    <PageContainer title="Daily Sales" subtitle="Revenue and units sold per day">
      <Card>
        <CardContent>
          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
            <TextField
              type="date"
              size="small"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={start}
              onChange={(e) => setStart(e.target.value)}
              inputProps={{ max: end || iso(new Date()) }}
            />
            <TextField
              type="date"
              size="small"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              inputProps={{ min: start }}
            />
          </Stack>

          <Box sx={{ height: "62vh" }}>
            {isLoading ? null : series.length === 0 ? (
              <Empty />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 16, right: 24, bottom: 8, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={p.divider} />
                  <XAxis dataKey="label" tick={axisStyle} stroke={p.divider} minTickGap={20} />
                  <YAxis
                    yAxisId="left"
                    tick={axisStyle}
                    stroke={p.divider}
                    width={72}
                    tickFormatter={(v) => formatCurrency(v).replace(/\.00$/, "")}
                  />
                  <YAxis yAxisId="right" orientation="right" tick={axisStyle} stroke={p.divider} width={40} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: p.background.paper,
                      border: `1px solid ${p.divider}`,
                      borderRadius: 8,
                      color: p.text.primary,
                    }}
                    formatter={(value, name) => (name === "Revenue" ? formatCurrency(value) : value)}
                  />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="revenue"
                    name="Revenue"
                    stroke={p.primary.main}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="units"
                    name="Units"
                    stroke={p.secondary.main}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Box>
        </CardContent>
      </Card>
    </PageContainer>
  );
};

const Empty = () => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "text.secondary" }}>
    <Typography variant="body2">No sales data in this range</Typography>
  </Box>
);

export default Daily;
